import type { TenantAiContextPayload } from '@/api/schema/data-contracts';

function hasNonEmptyStringInRecord(obj: Record<string, unknown> | null | undefined): boolean {
  if (!obj) return false;
  return Object.values(obj).some((v) => typeof v === 'string' && v.trim().length > 0);
}

export const TOTAL_SECTIONS = 10;

/** Number of top-level sections (1–10) with at least one non-empty field. */
export function countConfiguredSections(data: TenantAiContextPayload | null | undefined): number {
  if (!data) return 0;
  let n = 0;
  if (hasNonEmptyStringInRecord(data.tenantIdentity as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.geography as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.regulatory as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.technology as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.organizational as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.securityPosture as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.nonFunctionalDefaults as Record<string, unknown> | undefined))
    n++;
  if (hasNonEmptyStringInRecord(data.resourceCosts as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.riskCulture as Record<string, unknown> | undefined)) n++;
  if (hasNonEmptyStringInRecord(data.mandatoryDeliverables as Record<string, unknown> | undefined))
    n++;
  return n;
}

function trimOrUndefined(s: string | undefined | null): string | undefined {
  if (s == null) return undefined;
  const t = s.trim();
  return t.length === 0 ? undefined : t;
}

/** Strip empty strings and omit empty section objects. Returns null when all fields are blank. */
export function normalizeTenantContextForSave(
  data: TenantAiContextPayload,
): TenantAiContextPayload | null {
  const tenantIdentity =
    data.tenantIdentity &&
    ({
      name: trimOrUndefined(data.tenantIdentity.name),
      industry: trimOrUndefined(data.tenantIdentity.industry),
      subIndustry: trimOrUndefined(data.tenantIdentity.subIndustry),
      companySize: trimOrUndefined(data.tenantIdentity.companySize),
    } as TenantAiContextPayload['tenantIdentity']);
  const ti =
    tenantIdentity && Object.values(tenantIdentity).some((v) => v !== undefined)
      ? tenantIdentity
      : undefined;

  const geography =
    data.geography &&
    ({
      countries: trimOrUndefined(data.geography.countries),
      primaryRegions: trimOrUndefined(data.geography.primaryRegions),
      languages: trimOrUndefined(data.geography.languages),
      timeZones: trimOrUndefined(data.geography.timeZones),
    } as TenantAiContextPayload['geography']);
  const g =
    geography && Object.values(geography).some((v) => v !== undefined) ? geography : undefined;

  const regulatory =
    data.regulatory &&
    ({
      applicableRegulations: trimOrUndefined(data.regulatory.applicableRegulations),
      dataSensitivity: trimOrUndefined(data.regulatory.dataSensitivity),
      compliancePosture: trimOrUndefined(data.regulatory.compliancePosture),
    } as TenantAiContextPayload['regulatory']);
  const r =
    regulatory && Object.values(regulatory).some((v) => v !== undefined) ? regulatory : undefined;

  const technology =
    data.technology &&
    ({
      hostingModel: trimOrUndefined(data.technology.hostingModel),
      cloudProviders: trimOrUndefined(data.technology.cloudProviders),
      techStack: trimOrUndefined(data.technology.techStack),
      integrationPatterns: trimOrUndefined(data.technology.integrationPatterns),
      databases: trimOrUndefined(data.technology.databases),
      devOpsTooling: trimOrUndefined(data.technology.devOpsTooling),
    } as TenantAiContextPayload['technology']);
  const tech =
    technology && Object.values(technology).some((v) => v !== undefined) ? technology : undefined;

  const organizational =
    data.organizational &&
    ({
      deliveryModel: trimOrUndefined(data.organizational.deliveryModel),
      methodologies: trimOrUndefined(data.organizational.methodologies),
      waysOfWorking: trimOrUndefined(data.organizational.waysOfWorking),
    } as TenantAiContextPayload['organizational']);
  const o =
    organizational && Object.values(organizational).some((v) => v !== undefined)
      ? organizational
      : undefined;

  const securityPosture =
    data.securityPosture &&
    ({
      securityLevel: trimOrUndefined(data.securityPosture.securityLevel),
      identityStandards: trimOrUndefined(data.securityPosture.identityStandards),
      dataClassification: trimOrUndefined(data.securityPosture.dataClassification),
      securityReviewExpectation: trimOrUndefined(data.securityPosture.securityReviewExpectation),
    } as TenantAiContextPayload['securityPosture']);
  const sp =
    securityPosture && Object.values(securityPosture).some((v) => v !== undefined)
      ? securityPosture
      : undefined;

  const nonFunctionalDefaults =
    data.nonFunctionalDefaults &&
    ({
      availabilityTarget: trimOrUndefined(data.nonFunctionalDefaults.availabilityTarget),
      performanceExpectations: trimOrUndefined(data.nonFunctionalDefaults.performanceExpectations),
      supportHours: trimOrUndefined(data.nonFunctionalDefaults.supportHours),
      environments: trimOrUndefined(data.nonFunctionalDefaults.environments),
      documentationExpectations: trimOrUndefined(
        data.nonFunctionalDefaults.documentationExpectations,
      ),
    } as TenantAiContextPayload['nonFunctionalDefaults']);
  const nf =
    nonFunctionalDefaults && Object.values(nonFunctionalDefaults).some((v) => v !== undefined)
      ? nonFunctionalDefaults
      : undefined;

  const resourceCosts =
    data.resourceCosts?.dayRatesByCategory && trimOrUndefined(data.resourceCosts.dayRatesByCategory)
      ? { dayRatesByCategory: trimOrUndefined(data.resourceCosts.dayRatesByCategory)! }
      : undefined;

  const riskCulture =
    data.riskCulture &&
    ({
      riskAppetite: trimOrUndefined(data.riskCulture.riskAppetite),
      changeManagementRigor: trimOrUndefined(data.riskCulture.changeManagementRigor),
      projectOversightStyle: trimOrUndefined(data.riskCulture.projectOversightStyle),
    } as TenantAiContextPayload['riskCulture']);
  const rc =
    riskCulture && Object.values(riskCulture).some((v) => v !== undefined)
      ? riskCulture
      : undefined;

  const mandatoryDeliverables =
    data.mandatoryDeliverables?.deliverablesList &&
    trimOrUndefined(data.mandatoryDeliverables.deliverablesList)
      ? {
          deliverablesList: trimOrUndefined(data.mandatoryDeliverables.deliverablesList)!,
        }
      : undefined;

  const result: TenantAiContextPayload = {
    tenantIdentity: ti,
    geography: g,
    regulatory: r,
    technology: tech,
    organizational: o,
    securityPosture: sp,
    nonFunctionalDefaults: nf,
    resourceCosts,
    riskCulture: rc,
    mandatoryDeliverables,
  };

  const any = ti || g || r || tech || o || sp || nf || resourceCosts || rc || mandatoryDeliverables;
  return any ? result : null;
}

export function mergeTenantContext(
  loaded: TenantAiContextPayload | null | undefined,
): TenantAiContextPayload {
  const d = defaultTenantContextForm();
  if (!loaded) return d;
  return {
    tenantIdentity: { ...d.tenantIdentity, ...loaded.tenantIdentity },
    geography: { ...d.geography, ...loaded.geography },
    regulatory: { ...d.regulatory, ...loaded.regulatory },
    technology: { ...d.technology, ...loaded.technology },
    organizational: { ...d.organizational, ...loaded.organizational },
    securityPosture: { ...d.securityPosture, ...loaded.securityPosture },
    nonFunctionalDefaults: { ...d.nonFunctionalDefaults, ...loaded.nonFunctionalDefaults },
    resourceCosts: { ...d.resourceCosts, ...loaded.resourceCosts },
    riskCulture: { ...d.riskCulture, ...loaded.riskCulture },
    mandatoryDeliverables: { ...d.mandatoryDeliverables, ...loaded.mandatoryDeliverables },
  };
}

export function defaultTenantContextForm(): TenantAiContextPayload {
  return {
    tenantIdentity: {},
    geography: {},
    regulatory: {},
    technology: {},
    organizational: {},
    securityPosture: {},
    nonFunctionalDefaults: {},
    resourceCosts: {},
    riskCulture: {},
    mandatoryDeliverables: {},
  };
}
